import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getListings, getShowings, getLeases, createCheckout, getAiMatches, uploadVideoIntro } from "../lib/api";
import Navbar from "../components/Navbar";
import PropertyCard from "../components/PropertyCard";
import VideoUploader from "../components/VideoUploader";
import LeaseCard from "../components/LeaseCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Search, Shield, Calendar, FileText, CheckCircle, Sparkles, ArrowRight, Clock
} from "lucide-react";

export default function RenterDashboard() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [showings, setShowings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingShowings, setLoadingShowings] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [showRes, leaseRes] = await Promise.all([getShowings(), getLeases()]);
        setShowings(showRes.data);
        setLeases(leaseRes.data);
      } catch (err) { console.error(err); }
      setLoadingShowings(false);
    })();
  }, []);

  const handleGetMatches = async () => {
    setLoadingMatches(true);
    try {
      const res = await getAiMatches();
      setMatches(res.data.matches || []);
    } catch (err) { console.error(err); }
    setLoadingMatches(false);
  };

  const bgStatus = user?.background_check_status;

  const handleBackgroundCheck = async () => {
    try {
      const res = await createCheckout("background_check", window.location.origin);
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces']" data-testid="renter-dashboard-heading">
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-[#5a5a5a] mt-1">Find your next home</p>
          </div>
          <Button data-testid="browse-listings-btn" onClick={() => window.location.href = "/browse"} className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-6">
            <Search className="w-4 h-4 mr-2" /> Browse Listings
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm" data-testid="bg-check-card">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bgStatus === "completed" ? "bg-[#1a6b3c]/10" : "bg-[#e5e3de]"}`}>
                <Shield className={`w-5 h-5 ${bgStatus === "completed" ? "text-[#1a6b3c]" : "text-[#5a5a5a]"}`} />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Background Check</p>
                <Badge className={`text-xs mt-1 ${bgStatus === "completed" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" : "bg-[#e5e3de] text-[#5a5a5a]"} border-0`}>
                  {bgStatus === "completed" ? "Completed" : "Not Started"}
                </Badge>
              </div>
            </div>
            {bgStatus !== "completed" && (
              <Button
                data-testid="start-bg-check-btn"
                onClick={handleBackgroundCheck}
                size="sm"
                className="w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] text-sm font-semibold"
              >
                Start Check — $36.05
              </Button>
            )}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#1a6b3c]/10">
                <Calendar className="w-5 h-5 text-[#1a6b3c]" />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Upcoming Showings</p>
                <p className="text-2xl font-bold text-[#0d0d0d]">{showings.filter(s => s.status === "pending" || s.status === "confirmed").length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#1a6b3c]/10">
                <FileText className="w-5 h-5 text-[#1a6b3c]" />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Active Leases</p>
                <p className="text-2xl font-bold text-[#0d0d0d]">{leases.filter(l => l.status === "active").length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Video Intro Section */}
        <div className="mb-8" data-testid="video-intro-section">
          <VideoUploader
            currentVideoPath={user?.video_intro_path}
            onUpload={async (file) => {
              await uploadVideoIntro(file);
              window.location.reload();
            }}
            title="Upload Your Intro Video"
            subtitle="Required — Landlords see this when you apply"
            maxDurationLabel="30–90 seconds"
            prompts={[
              "Who you are",
              "What you do",
              "Lifestyle & habits",
              "Cleanliness & schedule",
              "What you're looking for",
            ]}
          />
        </div>

        <Tabs defaultValue="matches" className="space-y-6">
          <TabsList className="bg-white border border-[#d5d3ce]/60 rounded-xl p-1">
            <TabsTrigger value="matches" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-matches">
              <Sparkles className="w-4 h-4 mr-1" /> AI Matches
            </TabsTrigger>
            <TabsTrigger value="showings" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-showings">
              <Calendar className="w-4 h-4 mr-1" /> Showings
            </TabsTrigger>
            <TabsTrigger value="leases" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-leases">
              <FileText className="w-4 h-4 mr-1" /> Leases
            </TabsTrigger>
          </TabsList>

          <TabsContent value="matches">
            {matches.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <Sparkles className="w-10 h-10 text-[#c8f563] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2 font-['Fraunces']">Get AI-Powered Matches</h3>
                <p className="text-[#5a5a5a] text-sm mb-4">Our AI will analyze listings and find the best matches for your needs.</p>
                <Button data-testid="get-matches-btn" onClick={handleGetMatches} disabled={loadingMatches} className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold">
                  {loadingMatches ? "Finding matches..." : "Find My Matches"}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="matches-grid">
                {matches.map(m => (
                  <PropertyCard key={m.listing_id} listing={m} matchScore={m.match_score} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="showings">
            {showings.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <Calendar className="w-10 h-10 text-[#d5d3ce] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2">No showings yet</h3>
                <p className="text-[#5a5a5a] text-sm">Browse listings and schedule showings to see them here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {showings.map(s => (
                  <div key={s.showing_id} className="bg-white rounded-xl p-4 border border-[#d5d3ce]/60 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#0d0d0d]">{s.listing_title}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[#5a5a5a]">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {s.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.time}</span>
                      </div>
                    </div>
                    <Badge className={`rounded-full text-xs ${
                      s.status === "confirmed" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" :
                      s.status === "cancelled" ? "bg-red-100 text-red-600" :
                      "bg-[#e5e3de] text-[#5a5a5a]"
                    } border-0`}>
                      {s.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leases">
            {leases.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <FileText className="w-10 h-10 text-[#d5d3ce] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2">No leases yet</h3>
                <p className="text-[#5a5a5a] text-sm">When a landlord sends you a lease offer, it will appear here to review and sign.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="renter-leases-list">
                {leases.map((l) => (
                  <LeaseCard
                    key={l.lease_id}
                    lease={l}
                    currentUserId={user?.user_id}
                    onUpdate={(updated) =>
                      setLeases((prev) => prev.map((x) => (x.lease_id === updated.lease_id ? updated : x)))
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
